import {styled} from '@linaria/react';
import React, {FC, PropsWithChildren, useEffect, useState} from 'react';
import {COLORS} from '../../../constants/colors';
import {useManualTimeout} from '../../../hooks/useManualTimeout/useManualTimeout';
import {Icon} from '../../atoms/Icon/Icon';
import {GenericIconName, IconName} from '../../atoms/Icon/IconName';
import {WishlistIconName} from './assets';

const HEART_ASSET: Record<ConfigProps['variant'], IconName> = {
  filled: GenericIconName.HeartFilled,
  'not-filled-with-outline': WishlistIconName.WishlistEmpty,
  'not-filled': GenericIconName.Heart,
};

type ConfigProps = {
  variant: 'filled' | 'not-filled' | 'not-filled-with-outline';
  withBackground?: boolean;
  size?: 'normal' | 'large';
  hovered: boolean;
  hasClicked: boolean;
  resetClick: VoidFunction;
};

type WishButtonProps = PropsWithChildren<ConfigProps>;

const scaleDuration = 500;

export const AnimatedWishButton: FC<WishButtonProps> = ({
  variant = 'not-filled-with-outline',
  withBackground,
  size,
  hovered,
  hasClicked,
  resetClick,
}) => {
  const [hasTimedOut, startTimer] = useManualTimeout(scaleDuration);
  const heartAsset = HEART_ASSET[variant];
  const hasJustBeenClicked = hasClicked && !hasTimedOut;

  useEffect(() => {
    if (hasClicked) {
      startTimer();
    }
  }, [hasClicked]);

  useEffect(() => {
    if (hasTimedOut) {
      resetClick();
    }
  }, [hasTimedOut]);

  return (
    <>
      <StyledIcon
        data-testid={`WishListIcon-${
          variant === 'filled' ? 'filled' : 'empty'
        }`}
        data-pulse={hovered && !hasJustBeenClicked && variant !== 'filled'}
        data-scale-up={hasJustBeenClicked && variant === 'filled'}
        data-scale-down={hasJustBeenClicked && variant === 'filled'}
        size={size === 'large' ? 'M' : 'S'}
        asset={heartAsset}
        fill={variant === 'filled' ? COLORS['Text.Black'] : COLORS['Text.Grey']}
      />
      {withBackground && (
        <StyledBackground
          size={size === 'large' ? 'M' : 'S'}
          asset={GenericIconName.HeartFilled}
          fill={COLORS.White}
        />
      )}
    </>
  );
};

export const useAnimatedWishButton = () => {
  const [hasClicked, setHasClicked] = useState(false);
  const handleHasClicked = () => setHasClicked(true);
  const resetClick = () => setHasClicked(false);

  return {hasClicked, resetClick, handleHasClicked};
};

const StyledIcon = styled(Icon)`
  display: block;

  &[data-pulse='true'] {
    animation: pulse 2s infinite;
    animation-timing-function: ease-in-out;
  }

  &[data-scale-up='true'] {
    animation: scaleUp ${scaleDuration}ms;
    animation-timing-function: ease-in-out;
  }

  &[data-scale-down='true'] {
    animation: scaleUp ${scaleDuration}ms;
    animation-timing-function: ease-in-out;
  }

  @keyframes pulse {
    0% {
      transform: scale(1);
    }
    25% {
      transform: scale(0.75);
    }
    50% {
      transform: scale(1);
    }
    75% {
      transform: scale(0.75);
    }
    100% {
      transform: scale(1);
    }
  }

  @keyframes scaleUp {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.5);
    }
    100% {
      transform: scale(1);
    }
  }

  @keyframes downScale {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(0.75);
    }
    100% {
      transform: scale(1);
    }
  }
`;

const StyledBackground = styled(Icon)`
  position: absolute;
  z-index: -1;
  stroke: white;
  stroke-width: 4px;
`;
